import type { MouseEvent } from "react";
import { Heart, Pencil } from "lucide-react";
import type { Product } from "../../../lib/api";
import { formatMoney } from "../../../lib/money";
import { useShop, useWishlist } from "../../../stores/cart";
import { useStorefrontContext } from "./StorefrontContext";

export function isSoldOut(product: Product): boolean {
  return product.stock !== null && product.stock <= 0;
}

/** Price with the original struck through when on sale. */
export function Price({ product, large = false }: { product: Product; large?: boolean }) {
  const { store } = useStorefrontContext();
  const onSale = product.compareAtMinor !== null && product.compareAtMinor > product.priceMinor;
  return (
    <span className={large ? "zs-price zs-price--lg" : "zs-price"}>
      <span className={onSale ? "zs-price__now zs-price__now--sale" : "zs-price__now"}>
        {formatMoney(product.priceMinor, store.currency)}
      </span>
      {onSale ? (
        <s className="zs-price__was">{formatMoney(product.compareAtMinor!, store.currency)}</s>
      ) : null}
    </span>
  );
}

export function WishlistButton({ product, variant = "floating" }: { product: Product; variant?: "floating" | "inline" }) {
  const { slug, interactive } = useStorefrontContext();
  const saved = useWishlist(slug).includes(product.id);
  const toggle = useShop((s) => s.toggleWishlist);
  const label = saved ? `Remove ${product.name} from wishlist` : `Save ${product.name} to wishlist`;
  return (
    <button
      type="button"
      className={`zs-wish zs-wish--${variant}${saved ? " zs-wish--on" : ""}`}
      aria-label={label}
      aria-pressed={saved}
      title={saved ? "Saved" : "Save for later"}
      onClick={(e: MouseEvent) => {
        e.preventDefault();
        if (!interactive) return;
        e.stopPropagation();
        toggle(slug, product.id);
      }}
    >
      <Heart size={variant === "inline" ? 18 : 16} aria-hidden="true" />
      {variant === "inline" ? <span>{saved ? "Saved" : "Save"}</span> : null}
    </button>
  );
}

export function ProductCard({ product }: { product: Product }) {
  const { paths, go, interactive, onEditProduct } = useStorefrontContext();
  // In the editor, cards are part of a selectable block, not links.
  const interactiveTabIndex = interactive ? undefined : -1;
  const href = paths.product(product);
  const [first, second] = product.images;
  const onSale = product.compareAtMinor !== null && product.compareAtMinor > product.priceMinor;
  const soldOut = isSoldOut(product);

  return (
    <article className="zs-card">
      <a
        className="zs-card__link"
        href={href}
        onClick={(e) => {
          if (e.metaKey || e.ctrlKey) return;
          e.preventDefault();
          go(href);
        }}
        tabIndex={interactiveTabIndex}
      >
        <div className="zs-card__media">
          {first ? (
            <img
              className="zs-card__img"
              src={first.url}
              alt={first.alt || product.name}
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div className="zs-card__placeholder" aria-hidden="true" />
          )}
          {second ? (
            <img className="zs-card__img zs-card__img--alt" src={second.url} alt="" loading="lazy" aria-hidden="true" />
          ) : null}
          {soldOut ? (
            <span className="zs-tag zs-tag--muted">Sold out</span>
          ) : onSale ? (
            <span className="zs-tag">Sale</span>
          ) : null}
          {onEditProduct && !interactive ? (
            <button
              type="button"
              className="zs-card__edit"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onEditProduct(product);
              }}
            >
              <Pencil size={13} aria-hidden="true" />
              Edit product
            </button>
          ) : null}
        </div>
        <div className="zs-card__body">
          {product.collection ? <span className="zs-card__collection">{product.collection}</span> : null}
          <h3 className="zs-card__name">{product.name}</h3>
          <Price product={product} />
        </div>
      </a>
    </article>
  );
}
